import EditButton from './EditButton'
import {FC, useState} from 'react'
import {toAbsoluteUrl} from '_metronic/helpers'
import Card from './Card'
import {deleteObject, ref} from 'firebase/storage'
import db, {storage} from 'services/firebase'
import {useAuth} from 'app/modules/auth'
import AddEditDocumentModal from './modals/AddEditDocumentModal'
import {addDoc, collection, deleteDoc, doc, serverTimestamp} from '@firebase/firestore'
import {ToastContainer} from 'react-toastify'
import Button from 'app/components/Button'
import {showSuccessToast} from 'app/components/ShowToast'
import ConfirmationModal from 'app/components/ConfirmationModal'
import CustomDrawer from './Drawer'

export type DocumentDataType = {
  id: string
  title: string
  documentURL: string | File
  documentName: string
  documentId?: string
  access: string
  isCustomDocumentUploaded?: boolean
}

type Props = {
  addedDocuments: DocumentDataType[]
}

const Documents: FC<Props> = ({addedDocuments}) => {
  const {currentUser} = useAuth()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [toEditDocument, setToEditDocument] = useState<DocumentDataType>({
    id: '',
    title: '',
    documentName: '',
    documentURL: '',
    access: '',
  })

  const [toDeleteDocument, setToDeleteDocument] = useState<DocumentDataType>()

  const [loading, setLoading] = useState(false)

  const onCancel = () => {
    setIsDrawerOpen(false)
    document.body.style.overflow = ''
    document.body.style.paddingRight = ''
  }

  const handleRemoveDocument = async () => {
    setLoading(true)
    try {
      const tenantId = currentUser?.tenantId || ''
      const documentsRef = collection(
        db,
        'tenants',
        tenantId,
        'trust_center',
        'documents',
        'all_documents'
      )

      if (toDeleteDocument?.isCustomDocumentUploaded) {
        const fileRef = ref(
          storage,
          `tenants/${currentUser?.tenantId}/trust_center/documents/${toDeleteDocument.documentId}-${toDeleteDocument?.documentName}`
        )

        await deleteObject(fileRef)
      }

      await deleteDoc(doc(documentsRef, toDeleteDocument?.id))

      const activitiesRef = collection(db, 'tenants', tenantId, 'activities')
      const currentDate = new Date()
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth() + 1
      const day = currentDate.getDate()
      const todaysDate = month + '/' + day + '/' + year

      await addDoc(activitiesRef, {
        message: `${toDeleteDocument?.title} document for trust center has been removed`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        username: currentUser?.userName,
        date: todaysDate,
        email: currentUser?.email,
        target: 'Trust Center',
        action: 'Document removed',
      })

      showSuccessToast({message: 'Document has been removed successfully'})
    } catch (error) {
      console.error('Error while removing document: ', error)
    } finally {
      setLoading(false)
      setToDeleteDocument(undefined)
      setIsDeleteModalOpen(false)
    }
  }

  return (
    <div>
      <CustomDrawer isDrawerOpen={isDrawerOpen} onCancel={onCancel} title='Documents'>
        <div style={{padding: '20px 20px 80px'}}>
          <p className='text-dark mb-6' style={{fontSize: '13px'}}>
            Add all resources like reports, certifications or white papers you would like to display
            in your Trust Center. You can assign restricted or public access to each documents.
          </p>

          <div className='d-flex flex-column'>
            <div className='d-flex justify-content-between align-items-center border-bottom border-gray-300 pb-1 mb-5'>
              <span className='text-dark fs-6 fw-bold'>Documents</span>

              <Button
                btnType='transparent'
                onClick={() => {
                  setToEditDocument({
                    id: '',
                    access: '',
                    documentName: '',
                    documentURL: '',
                    title: '',
                  })
                  setIsModalOpen(true)
                }}
              >
                <img
                  alt='plus-icon'
                  src={toAbsoluteUrl('/media/icon/plus.svg')}
                  className='me-2'
                  style={{height: '18px', width: '18px'}}
                />
                <span>Add Document</span>
              </Button>
            </div>

            {addedDocuments.length > 0 ? (
              addedDocuments.map((document, index) => (
                <div
                  key={document.id}
                  className='d-flex align-items-center'
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    gap: '12px',
                    padding: '20px',
                    marginBottom: '12px',
                  }}
                >
                  <div className='flex-fill d-flex align-items-center' style={{gap: '6px'}}>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{
                        backgroundColor: '#F3F4F6',
                        borderRadius: '12px',
                        height: '48px',
                        width: '48px',
                      }}
                    >
                      <img
                        alt='file-text-icon'
                        src={toAbsoluteUrl('/media/icon/file-text.svg')}
                        className=''
                        style={{height: '32px', width: '32px'}}
                      />
                    </div>

                    <h4 className='text-dark fs-6 fw-medium mb-0'>{document.title}</h4>
                  </div>

                  <div
                    className='d-flex align-items-center'
                    style={{
                      backgroundColor: '#F3F4F6',
                      border: '#1px solid #E5E7EB',
                      borderRadius: '6px',
                      gap: '4px',
                      padding: '2px 8px',
                    }}
                  >
                    <img
                      alt={`${document.access}-logo`}
                      src={`${
                        document.access === 'Restricted'
                          ? toAbsoluteUrl('/media/icon/lock.svg')
                          : toAbsoluteUrl('/media/icon/world.svg')
                      }`}
                      className=''
                      style={{height: '14px', width: '14px'}}
                    />
                    <span
                      className='fw-medium text-center fs-7'
                      style={{
                        color: '#1F2A37',
                      }}
                    >
                      {document.access}
                    </span>
                  </div>

                  <div className='d-flex' style={{gap: '8px'}}>
                    <Button
                      style={{
                        width: '34px',
                        height: '34px',
                      }}
                      onClick={() => {
                        setToEditDocument(document)
                        setIsModalOpen(true)
                      }}
                    >
                      <img
                        alt='edit-icon'
                        src={toAbsoluteUrl('/media/icon/edit.svg')}
                        className=''
                        style={{height: '18px', width: '18px'}}
                      />
                    </Button>

                    <Button
                      style={{
                        width: '34px',
                        height: '34px',
                      }}
                      onClick={() => {
                        setToDeleteDocument(document)
                        setIsDeleteModalOpen(true)
                      }}
                    >
                      <img
                        alt='trash-icon'
                        src={toAbsoluteUrl('/media/icon/trash.svg')}
                        className=''
                        style={{height: '18px', width: '18px'}}
                      />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <div
                className='w-100 d-flex justify-content-center align-items-center'
                style={{height: '245px'}}
              >
                <div className='d-flex flex-column align-items-center gap-4'>
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      backgroundColor: '#F3F4F6',
                      borderRadius: '12px',
                      height: '48px',
                      width: '48px',
                    }}
                  >
                    <img
                      alt='file-text-icon'
                      src={toAbsoluteUrl('/media/icon/file-text.svg')}
                      className=''
                      style={{height: '32px', width: '32px'}}
                    />
                  </div>

                  <span className='text-dark fs-6 fw-medium'>No documents added</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </CustomDrawer>

      {isModalOpen && (
        <AddEditDocumentModal
          isModalOpen={isModalOpen}
          hideModal={() => setIsModalOpen(false)}
          isEditMode={Boolean(toEditDocument.id)}
          document={toEditDocument}
        />
      )}

      <Card
        title='Documents'
        actionComponent={
          <EditButton
            onClickHandler={() => {
              setIsDrawerOpen(true)
              const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth
              document.body.style.overflow = 'hidden'
              document.body.style.paddingRight = `${scrollBarWidth}px`
            }}
          />
        }
      >
        {addedDocuments.length > 0 ? (
          <div className='row gy-5 gx-10 me-0'>
            {addedDocuments.map((document, index) => (
              <div key={index} className='col-12 col-md-6 pe-0'>
                <div
                  className='d-flex align-items-center'
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    gap: '6px',
                    padding: '20px',
                  }}
                >
                  <div className='flex-fill d-flex align-items-center' style={{gap: '12px'}}>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{
                        backgroundColor: '#F3F4F6',
                        borderRadius: '12px',
                        height: '48px',
                        width: '48px',
                      }}
                    >
                      <img
                        alt='file-text-icon'
                        src={toAbsoluteUrl('/media/icon/file-text.svg')}
                        className=''
                        style={{height: '32px', width: '32px'}}
                      />
                    </div>

                    <span className='fw-medium text-dark fs-6'>{document.title}</span>
                  </div>

                  <div
                    className='d-flex align-items-center'
                    style={{
                      backgroundColor: '#F3F4F6',
                      border: '#1px solid #E5E7EB',
                      borderRadius: '6px',
                      padding: '2px 8px',
                      gap: '4px',
                    }}
                  >
                    <img
                      alt={`${document.access}-logo`}
                      src={`${
                        document.access === 'Restricted'
                          ? toAbsoluteUrl('/media/icon/lock.svg')
                          : toAbsoluteUrl('/media/icon/world.svg')
                      }`}
                      className=''
                      style={{height: '14px', width: '14px'}}
                    />
                    <span
                      className='fw-medium fs-7 text-center'
                      style={{
                        color: '#1F2A37',
                      }}
                    >
                      {document.access}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            className='w-100 d-flex justify-content-center align-items-center'
            style={{height: '350px'}}
          >
            <div className='d-flex flex-column align-items-center gap-4'>
              <div
                className='d-flex align-items-center justify-content-center'
                style={{
                  backgroundColor: '#F3F4F6',
                  borderRadius: '12px',
                  height: '48px',
                  width: '48px',
                }}
              >
                <img
                  alt='file-text-icon'
                  src={toAbsoluteUrl('/media/icon/file-text.svg')}
                  className=''
                  style={{height: '32px', width: '32px'}}
                />
              </div>

              <span className='text-dark fs-6 fw-medium'>No documents added</span>
            </div>
          </div>
        )}
      </Card>

      {isDeleteModalOpen && (
        <ConfirmationModal
          isModalOpen={isDeleteModalOpen}
          hideModal={() => setIsDeleteModalOpen(false)}
          confirmBtnClickHandler={handleRemoveDocument}
          toDeleteItemTitle='document'
          isLoading={loading}
        />
      )}

      <ToastContainer />
    </div>
  )
}

export default Documents
